/**
 * Inbox poll-state (watermark) document schema and path.
 *
 * `pollInbox` keeps a single watermark document recording how far it has
 * ingested from the shared mailbox, so overlapping scheduler runs fetch only
 * new mail. The watermark is an IMAP UID: messages are ingested in increasing
 * UID order and `lastUid` is the highest UID successfully written.
 *
 * `uidValidity` guards against mailbox resets — when the server reports a
 * different `uidValidity` than the stored one, previously-seen UIDs are no
 * longer comparable and the watermark must be treated as reset (see
 * {@link resolvePollStart}). Server-written only.
 */
import {z} from 'zod';
import {FirestoreTimestampSchema} from '../../types';
import {
  AGENT_BUCKET_ID,
  AGENT_STATE_SUBCOLLECTION,
  getBucketDocPath,
} from '../reportBucket';

/**
 * Base schema for the singleton inbox watermark at `reports/_agent/state/inbox`.
 */
export const InboxPollStateDocumentSchema = z
  .object({
    /**
     * The mailbox's IMAP `UIDVALIDITY` at the time `lastUid` was recorded. A
     * change means UIDs were reassigned and `lastUid` is no longer meaningful.
     */
    uidValidity: z.number().int().nonnegative(),
    /** Highest IMAP UID successfully ingested so far. */
    lastUid: z.number().int().nonnegative(),
    /**
     * Server time the watermark last advanced — i.e. the most recent poll that
     * ingested at least one new message (or reset on a `UIDVALIDITY` change).
     * Empty polls deliberately do NOT update this to avoid a write per run;
     * poller liveness is observed via Cloud Functions metrics, not a Firestore
     * heartbeat.
     */
    lastAdvancedAt: FirestoreTimestampSchema,
  })
  .loose();

/** Inferred type for {@link InboxPollStateDocumentSchema}. */
export type InboxPollStateDocument = z.infer<
  typeof InboxPollStateDocumentSchema
>;

/** Document id of the singleton INBOX watermark. */
export const INBOX_POLL_STATE_DOC_ID = 'inbox' as const;

/** Document id of the singleton Sent-Mail watermark (outbound ingestion). */
export const SENT_POLL_STATE_DOC_ID = 'sent' as const;

/**
 * Path to a per-mailbox poll-state (watermark) document, nested under the
 * reserved `_agent` bucket's `state` subcollection. INBOX and Sent Mail each
 * get their own watermark since their UID spaces are independent.
 *
 * @param args.stateDocId - The watermark doc id ({@link INBOX_POLL_STATE_DOC_ID}
 *   or {@link SENT_POLL_STATE_DOC_ID}).
 * @returns Firestore doc path `reports/_agent/state/{stateDocId}`.
 * @throws Never — pure string construction.
 */
export function getPollStateDocumentPath({
  stateDocId,
}: {
  stateDocId: string;
}): string {
  return `${getBucketDocPath({bucketId: AGENT_BUCKET_ID})}/${AGENT_STATE_SUBCOLLECTION}/${stateDocId}`;
}

/**
 * Path to the singleton INBOX watermark document.
 *
 * @returns Firestore doc path `reports/_agent/state/inbox`.
 * @throws Never — pure string construction.
 */
export function getInboxPollStateDocumentPath(): string {
  return getPollStateDocumentPath({stateDocId: INBOX_POLL_STATE_DOC_ID});
}

/**
 * Path to the singleton Sent-Mail watermark document.
 *
 * @returns Firestore doc path `reports/_agent/state/sent`.
 * @throws Never — pure string construction.
 */
export function getSentPollStateDocumentPath(): string {
  return getPollStateDocumentPath({stateDocId: SENT_POLL_STATE_DOC_ID});
}
